import { useEffect, useState } from 'react'
import {
  Box,
  Card,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { Link } from 'react-router-dom'
import { useAppBase, appPath } from '../components/appBase'
import { eventService } from '../services/eventService'
import type { EventForm, FormStatus } from '../data/types'
import PageContainer from '../components/ui/PageContainer'
import { LoadingState } from '../components/ui/States'
import { getSubmittedFormIds } from '../services/formState'

const STATUS_META: Record<
  FormStatus,
  { label: string; color: string; bg: string }
> = {
  open: { label: 'Open', color: '#0075BC', bg: 'rgba(0,117,188,0.12)' },
  completed: { label: 'Completed', color: '#2e7d32', bg: 'rgba(46,125,50,0.12)' },
  closed: { label: 'Closed', color: '#707070', bg: 'rgba(112,112,112,0.12)' },
}

export default function FormsPage() {
  const [forms, setForms] = useState<EventForm[]>([])
  const [loading, setLoading] = useState(true)
  const submitted = getSubmittedFormIds()
  const base = useAppBase()

  useEffect(() => {
    eventService.getForms().then((f) => {
      setForms(f)
      setLoading(false)
    })
  }, [])

  if (loading) return <LoadingState />

  const withStatus = forms.map((f) => ({
    ...f,
    status: submitted.has(f.id) ? ('completed' as FormStatus) : f.status,
  }))

  return (
    <PageContainer
      title="Forms & Surveys"
      subtitle="Everything we need from you — RSVPs, preferences, and feedback"
    >
      <Stack spacing={1.5}>
        {withStatus.map((form) => {
          const meta = STATUS_META[form.status]
          const disabled = form.status === 'closed'
          const card = (
            <Card
              variant="outlined"
              sx={{
                p: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                borderRadius: 3,
                opacity: disabled ? 0.6 : 1,
                textDecoration: 'none',
                color: 'inherit',
                transition: 'border-color .15s',
                '&:hover': disabled ? {} : { borderColor: 'primary.main' },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {form.title}
                  </Typography>
                  <Chip
                    label={meta.label}
                    size="small"
                    icon={
                      form.status === 'completed' ? (
                        <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
                      ) : form.status === 'closed' ? (
                        <LockRoundedIcon sx={{ fontSize: 14 }} />
                      ) : undefined
                    }
                    sx={{
                      height: 22,
                      fontWeight: 700,
                      fontSize: '0.68rem',
                      color: meta.color,
                      bgcolor: meta.bg,
                      '& .MuiChip-icon': { color: meta.color },
                    }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {form.description}
                </Typography>
                {form.dueLabel && (
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {form.dueLabel}
                  </Typography>
                )}
              </Box>
              {!disabled && <ChevronRightRoundedIcon color="action" />}
            </Card>
          )

          return disabled ? (
            <Box key={form.id}>{card}</Box>
          ) : (
            <Box
              key={form.id}
              component={Link}
              to={appPath(base, `forms/${form.id}`)}
              sx={{ textDecoration: 'none' }}
            >
              {card}
            </Box>
          )
        })}
      </Stack>
    </PageContainer>
  )
}
